/**
 * The signed-in session, as the browser holds it.
 *
 * Storage only — no network. `auth.ts` owns sign-in and sign-out, and `http.ts`
 * reads the token to authorise a request and drops it when the API says it is
 * no longer good. Both need these primitives, so they live below both rather
 * than in either one: putting them in `auth.ts` and having `http.ts` import it
 * makes the two modules mutually dependent, and whichever loads first hits the
 * other's uninitialised `BASE`.
 */

const STORAGE_KEY = 'qcomm.auth';
const ACTIVE_KEY = 'qcomm.profile';

export interface AuthUser {
  email?: string;
  firstName?: string;
  lastName?: string;
  fullName?: string;
  [k: string]: unknown;
}

/** One Blinkit brand this sign-in manages, as the platform describes it. */
export interface Profile {
  id: number | null;
  name: string;
  /** "Admin" | "Manager". Displayed only — the API enforces entitlement. */
  role: string | null;
  slug: string | null;
}

export interface Session {
  token: string;
  user: AuthUser | null;
  /** Brands this sign-in is entitled to; the API enforces the same list. */
  brands?: string[];
  /** The same grants with the detail the header shows. */
  profiles?: Profile[];
}

/**
 * Session lives in sessionStorage, not localStorage: the token is a bearer
 * credential and should not outlive the browser tab. It is deliberately not in
 * a cookie either — the API is a separate origin in production and this avoids
 * needing third-party cookie behaviour to keep working.
 */
export function loadSession(): Session | null {
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === 'object' &&
      parsed !== null &&
      typeof (parsed as Session).token === 'string'
    ) {
      return parsed as Session;
    }
    return null;
  } catch {
    return null;
  }
}

export function saveSession(session: Session): void {
  sessionStorage.setItem(STORAGE_KEY, JSON.stringify(session));
}

/**
 * Forget the local copy, without telling the server.
 *
 * Sign-out revokes first and then calls this. The expiry path calls it alone:
 * the token the API just refused is already dead, and posting it back to
 * `/v1/auth/session` would only be a second rejected request.
 */
export function dropLocalSession(): void {
  sessionStorage.removeItem(STORAGE_KEY);
  sessionStorage.removeItem(ACTIVE_KEY);
}

/**
 * One spelling of a brand name, for comparison only.
 *
 * Drops case and every non-alphanumeric character, so the straight and curly
 * apostrophes and the spaced and unspaced forms all collapse together. Never
 * shown to anyone and never sent anywhere - it exists solely to decide whether
 * two strings mean the same brand.
 */
const foldBrand = (s: string): string => s.toLowerCase().replace(/[^a-z0-9]/g, '');

/**
 * The profile the dashboard is currently scoped to.
 *
 * Kept beside the session rather than in the URL because it is not a view of
 * the page — it is WHOSE data the page is. It is also validated against the
 * session's grants on read: a stale value left over from a previous sign-in
 * would otherwise scope every request to a brand the API will refuse.
 */
export function loadActiveProfile(): string | null {
  const session = loadSession();
  if (!session) return null;
  const stored = sessionStorage.getItem(ACTIVE_KEY);
  const granted = session.profiles?.map((p) => p.name) ?? session.brands ?? [];
  if (stored && granted.includes(stored)) return stored;

  // No stored choice: pick a DETERMINISTIC one. The platform returns grants in
  // profileId order, which is arbitrary to a reader — landing on "Parul garg"
  // because it happens to be id 16 is a coin toss dressed as a decision.
  // Prefer the configured default when the user actually holds it, otherwise
  // the alphabetically first, so the same account always opens on the same
  // brand.
  // Matched on a FOLDED name, not an exact string. The same brand is spelled
  // several ways across the platform - "O'cean Beverages", the curly-apostrophe
  // "O’cean Beverages", and the punctuation-free key "oceanbeverages" - and an
  // exact comparison silently misses, falling through to the alphabetical
  // branch. That failure is invisible: the dashboard opens on a real brand,
  // just not the configured one. Whatever matches, the GRANTED spelling is
  // returned, because that exact string is what every request is scoped with.
  const preferred = import.meta.env.VITE_CUSTOMER_NAME;
  if (preferred) {
    const want = foldBrand(preferred);
    const hit = granted.find((g) => foldBrand(g) === want);
    if (hit) return hit;
  }
  return [...granted].sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))[0] ?? null;
}

export function saveActiveProfile(name: string): void {
  sessionStorage.setItem(ACTIVE_KEY, name);
}

/** Clears only the brand choice, so a fresh sign-in cannot inherit the last one. */
export function dropActiveProfile(): void {
  sessionStorage.removeItem(ACTIVE_KEY);
}
